// Reads a pending share_target payload from the URL query string (Android
// share menu via method:GET in the manifest). Submits it as a capture, then
// strips the query so a refresh doesn't re-submit.
import { submitUrl } from "./db";
import { navigate } from "./router";

export async function consumeShareTarget(): Promise<boolean> {
  const u = new URL(window.location.href);
  // Android Chrome usually delivers the shared link in `text` (sometimes
  // `title`), leaving `url` empty. Try url first, then extract the first
  // http(s) link from text/title.
  const direct = u.searchParams.get("url");
  const fromText = extractUrl(u.searchParams.get("text")) ?? extractUrl(u.searchParams.get("title"));
  const candidate = direct && /^https?:\/\//i.test(direct) ? direct : fromText;
  if (!candidate) {
    return false;
  }
  // Clean the URL so a refresh doesn't re-fire.
  const cleanUrl = `${u.origin}${u.pathname}`;
  window.history.replaceState(null, "", cleanUrl);

  try {
    const { id } = await submitUrl(candidate);
    navigate(`/articles/${id}`);
    return true;
  } catch (e) {
    // Surface the error in the inbox banner via a session flag.
    sessionStorage.setItem("stash.share-error", (e as Error).message);
    navigate("/");
    return false;
  }
}

export function takeShareError(): string | null {
  const v = sessionStorage.getItem("stash.share-error");
  if (v) sessionStorage.removeItem("stash.share-error");
  return v;
}

// Pull the first http(s) URL out of an arbitrary text blob (the `text`/`title`
// share params). Handles bare URLs and URLs wrapped in prose.
function extractUrl(text: string | null): string | null {
  if (!text) return null;
  const match = text.match(/https?:\/\/[^\s<>"']+/i);
  return match ? match[0] : null;
}
